'use client'

import { useEffect } from 'react'
import { MASTHEAD_MENU_ATTRIBUTE } from '@/components/editorial/masthead-menu'

/**
 * Progressive enhancement for the masthead's folded menu (below 1240px). The menu itself is a
 * native `<details>` disclosure — it opens, closes and announces its state
 * without JavaScript, and the primary nav that follows it is a plain `<nav>`
 * of anchors. This island only tidies the two things the platform leaves
 * open: a same-page hash link (Collection, Special Editions on the home page)
 * would otherwise leave the panel hanging over the chapter it just scrolled
 * to, and Escape should dismiss the panel and hand focus back to its summary.
 * Renders nothing.
 */
export default function MastheadMenu() {
  useEffect(() => {
    const menu = document.querySelector<HTMLDetailsElement>(`details[${MASTHEAD_MENU_ATTRIBUTE}]`)
    const nav = menu?.nextElementSibling
    if (!menu || !(nav instanceof HTMLElement)) return

    const close = () => {
      if (menu.open) menu.open = false
    }
    const onNavClick = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('a')) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !menu.open) return
      close()
      menu.querySelector<HTMLElement>('summary')?.focus()
    }
    nav.addEventListener('click', onNavClick)
    menu.addEventListener('keydown', onKeyDown)
    nav.addEventListener('keydown', onKeyDown)
    return () => {
      nav.removeEventListener('click', onNavClick)
      menu.removeEventListener('keydown', onKeyDown)
      nav.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  return null
}
